<template>
  <div class="container-fluid pa-page">
    <!-- Filtros -->
    <div class="pa-filtros mb-4">
      <div class="row align-items-end">
        <div class="col-md-8 col-12 mb-2 mb-md-0">
          <label class="pa-label">Setor</label>
          <v-select
            v-model="filtro.setor_id"
            :options="setores"
            label="nome"
            :reduce="(s) => s.id"
            placeholder="Todos os setores"
            @input="buscar"
          />
        </div>
        <div class="col-md-4 col-12 mb-2 mb-md-0">
          <label class="pa-label">Data</label>
          <input
            type="date"
            class="form-control pa-select"
            v-model="filtro.data"
            @change="buscar"
          />
        </div>
      </div>
    </div>

    <!-- KPIs -->
    <div class="row mb-4" v-if="!carregando && !semDados">
      <div class="col-md-3 col-6 mb-3">
        <div class="pa-kpi pa-kpi--green">
          <div class="pa-kpi__icon"><i class="fas fa-calendar-check"></i></div>
          <div class="pa-kpi__body">
            <div class="pa-kpi__value">{{ totalAgendado }}</div>
            <div class="pa-kpi__label">Agendados no dia</div>
          </div>
        </div>
      </div>
      <div class="col-md-3 col-6 mb-3">
        <div class="pa-kpi pa-kpi--blue">
          <div class="pa-kpi__icon"><i class="fas fa-user-check"></i></div>
          <div class="pa-kpi__body">
            <div class="pa-kpi__value">{{ totalCompareceram }}</div>
            <div class="pa-kpi__label">Compareceram</div>
          </div>
        </div>
      </div>
      <div class="col-md-3 col-6 mb-3">
        <div class="pa-kpi pa-kpi--red">
          <div class="pa-kpi__icon"><i class="fas fa-user-times"></i></div>
          <div class="pa-kpi__body">
            <div class="pa-kpi__value">{{ totalAusentes }}</div>
            <div class="pa-kpi__label">Ausentes</div>
          </div>
        </div>
      </div>
      <div class="col-md-3 col-6 mb-3">
        <div class="pa-kpi pa-kpi--purple">
          <div class="pa-kpi__icon"><i class="fas fa-percent"></i></div>
          <div class="pa-kpi__body">
            <div class="pa-kpi__value">{{ taxaComparecimento !== null ? taxaComparecimento + "%" : "—" }}</div>
            <div class="pa-kpi__label">Taxa de comparecimento</div>
            <div class="pa-kpi__sub" v-if="taxaComparecimento !== null">{{ totalCompareceram }} de {{ totalAgendado }} agendados</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Gráfico -->
    <div class="card pa-card mb-4">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="pa-card__title mb-0">Agendamentos por hora — {{ filtro.data | formatDate }}</h6>
          <span v-if="isToday" class="pa-live-badge">
            <span class="pa-live-dot"></span> Tempo real
          </span>
        </div>

        <div v-if="carregando" class="pa-empty">
          <i class="fas fa-spinner fa-spin fa-2x text-primary mb-2"></i>
          <div>Carregando...</div>
        </div>
        <div v-else-if="semDados" class="pa-empty">
          <i class="fas fa-calendar fa-3x mb-3" style="color: #dee2e6"></i>
          <div class="font-weight-bold mb-1">Nenhum agendamento</div>
          <small class="text-muted">Não há agendamentos registrados para esta data.</small>
        </div>
        <div v-else style="position: relative; height: 380px">
          <bar-chart :chart-data="chartData" :chart-options="chartOptions" />
        </div>
      </div>
    </div>

    <!-- Tabela -->
    <div class="card pa-card" v-if="!carregando && !semDados">
      <div class="card-body p-0">
        <div class="pa-table-header px-3 py-2">
          <h6 class="pa-card__title mb-0">Detalhamento por hora</h6>
        </div>
        <table class="table table-sm mb-0 pa-table">
          <thead>
            <tr>
              <th>Hora</th>
              <th class="text-center">Total</th>
              <th class="text-center">Compareceram</th>
              <th class="text-center">Ausentes</th>
              <th class="text-center">Pendentes</th>
              <th class="text-center" v-if="capacidadeSetor">Ocupação</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="hora in horasComDados"
              :key="hora"
              :class="{ 'pa-table__row--pico': hora === pico.hora }"
            >
              <td>
                <span class="pa-hora">{{ String(hora).padStart(2, "0") }}:00</span>
                <i v-if="hora === pico.hora" class="fas fa-fire ml-2 text-warning" title="Horário de pico"></i>
              </td>
              <td class="text-center font-weight-bold">{{ porHora[hora] ? porHora[hora].total : 0 }}</td>
              <td class="text-center">
                <span v-if="porHora[hora] && porHora[hora].compareceram" class="pa-status pa-status--ok">
                  {{ porHora[hora].compareceram }}
                </span>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-center">
                <span v-if="porHora[hora] && porHora[hora].ausentes" class="pa-status pa-status--ausente">
                  {{ porHora[hora].ausentes }}
                </span>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-center">
                <span v-if="porHora[hora] && porHora[hora].pendentes" class="pa-status pa-status--pendente">
                  {{ porHora[hora].pendentes }}
                </span>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-center" v-if="capacidadeSetor">
                <span v-if="porHora[hora] && porHora[hora].total >= capacidadeSetor" class="pa-ocupacao pa-ocupacao--critico">
                  <i class="fas fa-exclamation-triangle"></i> Estoura
                </span>
                <span v-else-if="quantidadeMaxAlerta && porHora[hora] && porHora[hora].total >= quantidadeMaxAlerta" class="pa-ocupacao pa-ocupacao--alerta">
                  <i class="fas fa-exclamation-circle"></i> Alerta
                </span>
                <span v-else-if="porHora[hora] && porHora[hora].total" class="pa-ocupacao pa-ocupacao--ok">
                  <i class="fas fa-check"></i> OK
                </span>
                <span v-else class="text-muted">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from "vue-select";
import BarChart from "@/components/BarChart.vue";

export default {
  name: "PrevisaoAgendamentos",
  components: { vSelect, BarChart },

  filters: {
    formatDate(val) {
      if (!val) return "";
      const [y, m, d] = val.split("-");
      return `${d}/${m}/${y}`;
    },
  },

  data() {
    return {
      carregando: false,
      setores: [],
      porHora: {},
      totalAgendado: 0,
      totalCompareceram: 0,
      totalAusentes: 0,
      pico: { hora: null, total: 0 },
      taxaComparecimento: null,
      capacidadeSetor: null,
      quantidadeMaxAlerta: null,
      isToday: true,
      filtro: {
        setor_id: null,
        data: new Date().toISOString().split("T")[0],
      },
    };
  },

  computed: {
    horasComDados() {
      return Object.keys(this.porHora).map(Number).sort((a, b) => a - b);
    },
    semDados() {
      return Object.keys(this.porHora).length === 0;
    },
    chartData() {
      const labels = Array.from({ length: 24 }, (_, i) => `${i}h`);

      const compareceuVals = labels.map((_, i) =>
        this.porHora[i] ? this.porHora[i].compareceram : 0
      );
      const ausenteVals = labels.map((_, i) =>
        this.porHora[i] ? this.porHora[i].ausentes : 0
      );
      const pendenteVals = labels.map((_, i) =>
        this.porHora[i] ? this.porHora[i].pendentes : 0
      );

      const datasets = [
        {
          label: "Compareceram",
          data: compareceuVals,
          backgroundColor: "rgba(18, 183, 106, 0.75)",
          borderColor: "rgba(18, 183, 106, 1)",
          borderWidth: 1,
          stack: "ag",
        },
        {
          label: "Ausentes",
          data: ausenteVals,
          backgroundColor: "rgba(240, 68, 56, 0.65)",
          borderColor: "rgba(240, 68, 56, 0.9)",
          borderWidth: 1,
          stack: "ag",
        },
        {
          label: "Pendentes",
          data: pendenteVals,
          backgroundColor: "rgba(160, 170, 190, 0.55)",
          borderColor: "rgba(160, 170, 190, 0.8)",
          borderWidth: 1,
          stack: "ag",
        },
      ];

      if (this.capacidadeSetor) {
        datasets.push({
          type: "line",
          label: "Capacidade máxima",
          data: Array(24).fill(this.capacidadeSetor),
          borderColor: "rgba(240, 68, 56, 0.85)",
          borderWidth: 2,
          borderDash: [6, 4],
          pointRadius: 0,
          fill: false,
        });
      }

      if (this.quantidadeMaxAlerta) {
        datasets.push({
          type: "line",
          label: "Limite de alerta",
          data: Array(24).fill(this.quantidadeMaxAlerta),
          borderColor: "rgba(255, 140, 0, 0.85)",
          borderWidth: 2,
          borderDash: [6, 4],
          pointRadius: 0,
          fill: false,
        });
      }

      return { labels, datasets };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: "top" },
        scales: {
          yAxes: [{ ticks: { beginAtZero: true, precision: 0 }, stacked: true }],
          xAxes: [{ gridLines: { display: false }, stacked: true }],
        },
        tooltips: {
          mode: "index",
          callbacks: {
            label(item, data) {
              const label = data.datasets[item.datasetIndex].label;
              const val = item.yLabel;
              return val > 0 || item.datasetIndex < 3 ? `${label}: ${val}` : null;
            },
          },
        },
      };
    },
  },

  methods: {
    buscar() {
      const self = this;
      self.carregando = true;

      const api = self.$store.state.api + "previsao-agendamentos";
      const params = { data: self.filtro.data };
      if (self.filtro.setor_id) params.setor_id = self.filtro.setor_id;

      axios
        .get(api, { params })
        .then((response) => {
          const data = response.data;
          self.porHora             = data.por_hora || {};
          self.totalAgendado       = data.total_agendado || 0;
          self.totalCompareceram   = data.total_compareceram || 0;
          self.totalAusentes       = data.total_ausentes || 0;
          self.pico                = data.pico || { hora: null, total: 0 };
          self.taxaComparecimento  = data.taxa_comparecimento ?? null;
          self.capacidadeSetor     = data.capacidade_setor ?? null;
          self.quantidadeMaxAlerta = data.quantidade_maxima_alerta ?? null;
          self.isToday             = data.is_today || false;
          if (data.setores && data.setores.length) {
            self.setores = data.setores;
          }
          self.carregando = false;
        })
        .catch((error) => {
          self.carregando = false;
          self.$message(
            "Erro",
            (error.response && error.response.data) || "Não foi possível carregar.",
            "error"
          );
        });
    },
  },

  mounted() {
    this.buscar();
  },
};
</script>

<style scoped>
.pa-page {
  background: #f4f6f9;
  min-height: 100vh;
  padding-top: 8px;
}
.pa-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #8a94a6;
  display: block;
  margin-bottom: 6px;
}
.pa-select {
  border-radius: 8px;
  border: 1px solid #e0e6ed;
  font-size: 14px;
  height: 38px;
}
.pa-kpi {
  border-radius: 14px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  background: #fff;
  border-left: 5px solid #e0e6ed;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  height: 100%;
}
.pa-kpi--green  { border-color: #12b76a; }
.pa-kpi--blue   { border-color: #4f6ef7; }
.pa-kpi--red    { border-color: #f04438; }
.pa-kpi--purple { border-color: #7c3aed; }
.pa-kpi__icon {
  font-size: 20px;
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #f4f6f9;
  color: #8a94a6;
}
.pa-kpi--green  .pa-kpi__icon { background: #ecfdf3; color: #12b76a; }
.pa-kpi--blue   .pa-kpi__icon { background: #eef2ff; color: #4f6ef7; }
.pa-kpi--red    .pa-kpi__icon { background: #fef3f2; color: #f04438; }
.pa-kpi--purple .pa-kpi__icon { background: #f5f0ff; color: #7c3aed; }
.pa-kpi__value {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.1;
  color: #1a2233;
}
.pa-kpi__label {
  font-size: 11px;
  color: #8a94a6;
  margin-top: 3px;
}
.pa-kpi__sub {
  font-size: 11px;
  color: #b0b8c8;
  margin-top: 2px;
}
.pa-card {
  border: none;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}
.pa-card__title {
  font-size: 14px;
  font-weight: 600;
  color: #1a2233;
}
.pa-live-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #ecfdf3;
  color: #12b76a;
  border-radius: 20px;
  padding: 3px 12px;
  font-size: 12px;
  font-weight: 600;
}
.pa-live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #12b76a;
  animation: pulse 1.5s infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.3; }
}
.pa-empty {
  text-align: center;
  padding: 60px 20px;
  color: #8a94a6;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.pa-table-header { border-bottom: 1px solid #f0f2f5; }
.pa-table thead th {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #8a94a6;
  border-top: none;
  border-bottom: 1px solid #f0f2f5;
  padding: 10px 16px;
}
.pa-table tbody td {
  padding: 10px 16px;
  vertical-align: middle;
  border-color: #f0f2f5;
  font-size: 13px;
}
.pa-table__row--pico { background: #f8ebba !important; }
.pa-hora {
  font-weight: 600;
  color: #1a2233;
  font-variant-numeric: tabular-nums;
}
.pa-status {
  display: inline-flex;
  align-items: center;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
}
.pa-status--ok      { background: #ecfdf3; color: #12b76a; }
.pa-status--ausente { background: #fef3f2; color: #f04438; }
.pa-status--pendente { background: #f0f2f5; color: #8a94a6; }
.pa-ocupacao {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
}
.pa-ocupacao--critico { background: #fef3f2; color: #f04438; }
.pa-ocupacao--alerta  { background: #fff4eb; color: #ff9d3d; }
.pa-ocupacao--ok      { background: #ecfdf3; color: #12b76a; }
</style>

<style>
.dark-layout .pa-page { background: #161d31; }
.dark-layout .pa-label { color: #676d7d; }
.dark-layout .pa-select { background: #283046; border-color: #3b4253; color: #d0d2d6; }
.dark-layout .pa-kpi { background: #283046; border-left-color: #3b4253; box-shadow: none; }
.dark-layout .pa-kpi--green  { border-left-color: #12b76a; }
.dark-layout .pa-kpi--blue   { border-left-color: #4f6ef7; }
.dark-layout .pa-kpi--red    { border-left-color: #f04438; }
.dark-layout .pa-kpi--purple { border-left-color: #7c3aed; }
.dark-layout .pa-kpi__icon { background: #1e2a3a; }
.dark-layout .pa-kpi--green  .pa-kpi__icon { background: rgba(18,183,106,0.15);  color: #12b76a; }
.dark-layout .pa-kpi--blue   .pa-kpi__icon { background: rgba(79,110,247,0.15);  color: #4f6ef7; }
.dark-layout .pa-kpi--red    .pa-kpi__icon { background: rgba(240,68,56,0.15);   color: #f04438; }
.dark-layout .pa-kpi--purple .pa-kpi__icon { background: rgba(124,58,237,0.15);  color: #a78bfa; }
.dark-layout .pa-kpi__value { color: #d0d2d6; }
.dark-layout .pa-kpi__label { color: #676d7d; }
.dark-layout .pa-card { background: #283046; box-shadow: none; }
.dark-layout .pa-card__title { color: #d0d2d6; }
.dark-layout .pa-live-badge { background: rgba(18,183,106,0.15); color: #12b76a; }
.dark-layout .pa-live-dot { background: #12b76a; }
.dark-layout .pa-empty { color: #676d7d; }
.dark-layout .pa-table-header { border-color: #3b4253; }
.dark-layout .pa-table thead th { color: #676d7d; border-color: #3b4253; }
.dark-layout .pa-table tbody td { border-color: #3b4253; color: #d0d2d6; }
.dark-layout .pa-table__row--pico { background: rgba(255,193,7,0.08) !important; }
.dark-layout .pa-hora { color: #d0d2d6; }
.dark-layout .pa-status--ok      { background: rgba(18,183,106,0.15);  color: #12b76a; }
.dark-layout .pa-status--ausente { background: rgba(240,68,56,0.15);   color: #f97066; }
.dark-layout .pa-status--pendente { background: rgba(160,170,190,0.15); color: #8a94a6; }
.dark-layout .pa-ocupacao--critico { background: rgba(240,68,56,0.15);  color: #f97066; }
.dark-layout .pa-ocupacao--alerta  { background: rgba(255,157,61,0.15); color: #ff9d3d; }
.dark-layout .pa-ocupacao--ok      { background: rgba(18,183,106,0.15); color: #12b76a; }
</style>
